import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useKeys } from '@portal/pkg/api/keys';
import type { Key } from '@portal/pkg/api/types';
import { type PageWidth, PortalPage } from '@portal/views/_shared/page';
import { PageLoader } from '@portal/views/_shared/page-loader';
import { LoadFailed } from '@portal/views/keys/LoadFailed';
import { findKey } from '@portal/views/keys/_shared';

interface KeyPageFrameProps {
	width?: PageWidth;

	children: ReactNode;
}

/**
 *	The chrome and the boundaries every single-key page shares. Its own
 *	`Suspense` so a refetch swaps the body for a loader instead of blanking the
 *	page, and its own `ErrorBoundary` so a failed read offers a retry rather
 *	than escaping to the router's, which only resets on navigation.
 */
export function KeyPageFrame({
	width = 'narrow',

	children,
}: KeyPageFrameProps): ReactNode {
	return (
		<PortalPage width={width}>
			<ErrorBoundary FallbackComponent={LoadFailed}>
				<Suspense fallback={<PageLoader />}>{children}</Suspense>
			</ErrorBoundary>
		</PortalPage>
	);
}

/**
 *	One key, out of the listing.
 *
 *	There is no endpoint for a single key, and there does not need to be: the
 *	listing nests everything a rotation replaced under its successor, so every
 *	key the organization has is in that one payload — and the detail page wants
 *	the whole chain anyway.
 */
export function useKey(id: Optional<string>): Optional<Key> {
	return findKey(useKeys(), id);
}
